<template>
  <ContextColorProvider context="brand">
    <div class="home-view">
      <HomeTemplate>
        <template #main-background>
          <HomeIllustration />
        </template>
        <template #main>
          <NewHomeAccountStatus
            v-if="
              accountStatusResponse?.accountStatus && shouldShowNewAccountStatus
            "
            :account-status-response="accountStatusResponse"
            :button-action="handleAccountStatusAction"
          />
          <HomeAccountStatus
            v-else-if="
              accountStatusResponse?.accountStatus &&
              !shouldShowNewAccountStatus
            "
            :account-status-response="accountStatusResponse"
          />
          <HomeSuitability
            v-else-if="
              accountStatusResponse &&
              !accountStatusResponse.hasInvestmentProfile
            "
          />
          <HomeBalanceSummary
            v-else-if="accountStatusResponse"
            :has-error="hasError"
            :balance-details="balanceDetails"
            :loading="isLoading"
            :is-overdue="isOverdue"
            @on-retry="reloadBalanceSummary"
          />
          <SectionError
            v-else-if="accountStatusError && !isRetryingAccountStatus"
            class="home-view__load-error"
            title="Não foi possível carregar seus dados neste momento"
            description="Aguarde alguns instantes e tente novamente."
            @on-retry="reloadAccountStatus"
          />
          <HomeBalanceSummaryLoader
            v-else
            class="home-view__account-status-loader"
          />
        </template>
        <template #content>
          <HomeActivities
            @open-register-panel="toogleOpenRegisterPanel"
            @open-orders-panel="openPendingOrders"
          />
          <HomeWidgets :show-investments-widgets="showInvestmentsWidgets" />
          <HomeNews />
        </template>
      </HomeTemplate>
    </div>
    <HomeRegister
      :is-open="openRegisterPanel"
      @close="toogleOpenRegisterPanel"
    />
    <PendingOrdersSheet
      :is-open="isPendingOrdersOpen"
      :order-type="pendingOrdersType"
      @close="isPendingOrdersOpen = false"
    />
    <AccountStateSideSheet
      :is-open="isAccountStateSideSheetOpen"
      @close="isAccountStateSideSheetOpen = false"
    />
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, ProvideReactive, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { ContextColorProvider } from '@warrenbrasil/nebraska-web';
import { HomeTemplate } from '@/modules/home/components/HomeTemplate';
import { HomeIllustration } from '@/modules/home/components/HomeIllustration';
import { HomeNews } from '@/modules/home/components/HomeNews';
import { HomeWidgets } from '@/modules/home/components/HomeWidgets';
import { HomeRegister } from '@/modules/home/components/HomeRegister';
import { HomeActivities } from '@/modules/home/components/HomeActivities';
import { clearNextRoutes } from '@/modules/common/store/RegulatoryUpdateRoutesOrchestrator';
import { UIModule } from '@/modules/common/store/UIModule';
import * as trackEvents from './track-events';

import {
  TotalBalanceResponse,
  getTotalBalance
} from '@/modules/home/services/bff';
import HomeBalanceSummary from '@/modules/home/components/HomeBalanceSummary/HomeBalanceSummary.vue';
import { HomeBalanceSummaryLoader } from '@/modules/home/components/HomeBalanceSummary/components/HomeBalanceSummaryLoader';
import { NewHomeAccountStatus } from '@/modules/home/components/NewHomeAccountStatus';
import HomeAccountStatus from '@/modules/home/components/HomeAccountStatus/HomeAccountStatus.vue';
import HomeSuitability from '@/modules/home/components/HomeSuitability/HomeSuitability.vue';
import { SectionError } from '@/modules/common/components/SectionError';
import { PendingOrdersSheet } from '@/modules/home/components/PendingOrdersSheet';
import { AccountStateSideSheet } from '@/modules/home/components/AccountStateSideSheet';
import {
  AccountStates,
  EAccountStatusAction
} from '@/modules/common/services/http/account';
import { ICustomer } from '@/types/models';
import { EButtonTypeV2 } from '@/modules/home/services/consolidation';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { getDomainAndPlatform } from '@/modules/common/helpers/onboarding/getDomainAndPlatform';

const CustomerModule = namespace('CustomerModule');

@Component({
  name: 'Home',
  components: {
    AccountStateSideSheet,
    ContextColorProvider,
    HomeAccountStatus,
    HomeActivities,
    HomeBalanceSummary,
    HomeBalanceSummaryLoader,
    HomeIllustration,
    HomeNews,
    HomeRegister,
    HomeSuitability,
    HomeTemplate,
    HomeWidgets,
    NewHomeAccountStatus,
    PendingOrdersSheet,
    SectionError
  }
})
export default class Home extends Vue {
  balanceDetails: Reactive<TotalBalanceResponse> = null;

  isLoading = false;
  hasError = false;
  isRetryingAccountStatus = false;
  openRegisterPanel = false;
  isPendingOrdersOpen = false;
  pendingOrdersType = EButtonTypeV2.INVESTMENT;
  isAccountStateSideSheetOpen = false;

  @CustomerModule.Getter('isRegulatoryStatusBlocked')
  readonly isOverdue!: boolean;

  @CustomerModule.Getter('getCustomerId')
  readonly customerApiId!: string;

  @CustomerModule.Getter('getCustomer')
  readonly customer!: ICustomer;

  @CustomerModule.Getter('getAccountStatus')
  readonly accountStatusResponse!: AccountStates;

  @CustomerModule.Getter('getAccountStatusError')
  readonly accountStatusError!: boolean;

  @CustomerModule.Action('fetchCustomerAccountStatus')
  readonly fetchCustomerAccountStatus!: () => Promise<void>;

  @ProvideReactive()
  get shouldTriggerHomeAnimations() {
    return !UIModule.getIsOpenWelcomeCurtain;
  }

  get showInvestmentsWidgets() {
    return this.accountStatusResponse?.clientEnabled ?? false;
  }

  get shouldShowNewAccountStatus() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_NEW_ACCOUNT_STATES);
  }

  openPendingOrders(type: EButtonTypeV2) {
    this.pendingOrdersType = type;
    this.isPendingOrdersOpen = true;
  }

  created() {
    this.load();
  }

  mounted() {
    trackEvents.trackHomePageView();
    clearNextRoutes();
  }

  reloadBalanceSummary() {
    this.load();
    trackEvents.trackReloadWorthErrorClick();
  }

  async reloadAccountStatus() {
    this.isRetryingAccountStatus = true;

    try {
      await this.fetchCustomerAccountStatus();
    } finally {
      this.isRetryingAccountStatus = false;
    }
  }

  toogleOpenRegisterPanel() {
    this.openRegisterPanel = !this.openRegisterPanel;
  }

  async load() {
    this.isLoading = true;
    this.hasError = false;

    try {
      await this.loadHeroBalance();
    } catch {
      this.hasError = true;
      trackEvents.trackWorthErrorLoad();
    } finally {
      this.isLoading = false;
    }
  }

  private async loadHeroBalance() {
    this.balanceDetails = await getTotalBalance();
  }

  openAccountStateSideSheet() {
    this.isAccountStateSideSheetOpen = true;
  }

  handleAccountStatusAction() {
    const button = this.accountStatusResponse?.accountStatus?.button;
    if (!button?.action) return;

    const actionsToOpenSideSheet = [
      EAccountStatusAction.RegisterDocuments,
      EAccountStatusAction.RegisterFinancialLife,
      EAccountStatusAction.RegisterPersonal
    ];

    if (actionsToOpenSideSheet.includes(button.action)) {
      this.openAccountStateSideSheet();
      return;
    }

    const { domain, platform } = getDomainAndPlatform();

    const routeMapping: Partial<
      Record<
        EAccountStatusAction,
        { mobile: string; desktop: string; withHost?: boolean }
      >
    > = {
      [EAccountStatusAction.RegisterSuitability]: {
        mobile: 'suitability',
        desktop: 'suitability'
      },
      [EAccountStatusAction.FacematchRejected]: {
        mobile: 'states/face-match',
        desktop: 'states/face-match'
      },
      [EAccountStatusAction.FacematchRequired]: {
        mobile: 'states/face-match',
        desktop: 'states/face-match'
      },
      [EAccountStatusAction.FacematchError]: {
        mobile: 'states/facematch-error',
        desktop: 'states/facematch-error'
      },
      [EAccountStatusAction.RegisterAddressReview]: {
        mobile: 'states/register-address-review',
        desktop: 'states/register-address-review'
      },
      [EAccountStatusAction.RegisterRejected]: {
        mobile: 'states/register-rejected',
        desktop: 'states/register-rejected'
      },
      [EAccountStatusAction.RegisterAnalysis]: {
        mobile: 'states/register-analysis',
        desktop: 'states/register-analysis'
      },
      [EAccountStatusAction.RegisterMinorsPending]: {
        mobile:
          'https://faq.warren.com.br/hc/pt-br/articles/215877547-Posso-abrir-conta-para-o-meu-filho-Existe-idade-m%C3%ADnima',
        desktop:
          'https://faq.warren.com.br/hc/pt-br/articles/215877547-Posso-abrir-conta-para-o-meu-filho-Existe-idade-m%C3%ADnima',
        withHost: true
      }
    };

    const route = routeMapping[button.action];
    if (!route) return;

    const routePath = route[platform];
    const href = route.withHost
      ? routePath
      : `/onboarding/${domain}/${platform}/${routePath}`;

    window.location.href = href;
  }
}
</script>

<style lang="less" scoped>
@max-width: 1008px;

.home-view__load-error,
.home-view__account-status-loader {
  display: block;
  max-width: @max-width;
  margin: @size-spacing-x1000 auto;

  :deep(.base-text) {
    color: @text-over-image !important;
  }
}
</style>
